import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CurrentUserService } from '@app/core/services/current-user.service';
import { ReleaseNoteApi, UserApi } from '@app/data/api';
import { CurrentUserDto, ReleaseNoteContentCategory, ReleaseNoteContentDto, ReleaseNoteDto } from '@app/data/dto';
import { ButtonComponent } from '@app/shared/controls/app-button/app-button';
import { AppIcon } from '@app/shared/controls/app-icon/app-icon';
import { AppModal } from '@app/shared/controls/app-modal/app-modal';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import DOMPurify from 'dompurify';
import { firstValueFrom } from 'rxjs';

type ReleaseNoteFilter = 'All' | ReleaseNoteContentCategory;

interface ReleaseNoteFilterOption {
  readonly label: string;
  readonly value: ReleaseNoteFilter;
}

@Component({
  selector: 'app-release-note-modal',
  imports: [ButtonComponent, AppIcon, AppModal, CommonModule, FormsModule, TranslatePipe],
  templateUrl: './release-note-modal.html',
  styleUrl: './release-note-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReleaseNoteModal {
  private readonly releaseNoteApi = inject(ReleaseNoteApi);
  private readonly userApi = inject(UserApi);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly translate = inject(TranslateService);
  private loadedForUserId: string | null = null;

  readonly previewMode = input(false);
  readonly previewReleaseNote = input<ReleaseNoteDto | null>(null);
  readonly previewClosed = output<void>();

  protected readonly open = signal(false);
  protected readonly loading = signal(false);
  protected readonly releaseNote = signal<ReleaseNoteDto | null>(null);
  protected readonly selectedSlideId = signal<string | null>(null);
  protected readonly activeFilter = signal<ReleaseNoteFilter>('All');
  protected readonly detailsOpen = signal(false);
  protected readonly doNotShowAgain = signal(false);

  protected readonly orderedSlides = computed(() => [...(this.releaseNote()?.contents ?? [])]
    .sort((first, second) => first.sortOrder - second.sortOrder));

  protected readonly filterOptions = computed<readonly ReleaseNoteFilterOption[]>(() => {
    const categories = new Set(this.orderedSlides().map((slide) => slide.releaseNoteContentType));
    return [
      { label: this.translate.instant('shared.releaseNote.filters.all'), value: 'All' },
      ...Array.from(categories).map((category) => ({ label: this.categoryLabel(category), value: category })),
    ];
  });

  protected readonly visibleSlides = computed(() => {
    const activeFilter = this.activeFilter();
    const slides = this.orderedSlides();
    return activeFilter === 'All' ? slides : slides.filter((slide) => slide.releaseNoteContentType === activeFilter);
  });

  protected readonly selectedSlide = computed(() => {
    const selectedSlideId = this.selectedSlideId();
    return this.orderedSlides().find((slide) => slide.releaseNoteContentId === selectedSlideId) ?? this.visibleSlides()[0] ?? this.orderedSlides()[0] ?? null;
  });

  constructor() {
    effect(() => {
      if (this.previewMode()) {
        const note = this.previewReleaseNote();
        this.releaseNote.set(note);
        this.selectedSlideId.set(note?.contents[0]?.releaseNoteContentId ?? null);
        this.activeFilter.set('All');
        this.detailsOpen.set(false);
        this.open.set(!!note && note.contents.length > 0);
        return;
      }

      const currentUser = this.currentUserService.currentUser();
      if (currentUser?.id && currentUser.id !== this.loadedForUserId) {
        void this.loadReleaseNote(currentUser);
      }
    });
  }

  protected selectFilter(value: ReleaseNoteFilter): void {
    this.activeFilter.set(value);
    this.detailsOpen.set(false);
    this.selectedSlideId.set(this.visibleSlides()[0]?.releaseNoteContentId ?? null);
  }

  protected openSlide(slide: ReleaseNoteContentDto): void {
    this.selectedSlideId.set(slide.releaseNoteContentId);
    this.detailsOpen.set(true);
  }

  protected backToOverview(): void {
    this.detailsOpen.set(false);
  }

  protected nextSlide(): void {
    this.moveSlide(1);
  }

  protected previousSlide(): void {
    this.moveSlide(-1);
  }

  protected openReleaseNotes(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  protected close(): void {
    if (this.previewMode()) {
      this.previewClosed.emit();
    } else {
      void this.acknowledgeIfNeeded();
    }
    this.open.set(false);
  }

  protected categoryLabel(category: ReleaseNoteContentCategory): string {
    switch (category) {
      case 0:
        return this.translate.instant('shared.releaseNote.categories.security');
      case 2:
        return this.translate.instant('shared.releaseNote.categories.bugfix');
      case 3:
        return this.translate.instant('shared.releaseNote.categories.improvement');
      default:
        return this.translate.instant('shared.releaseNote.categories.feature');
    }
  }

  protected categoryTone(category: ReleaseNoteContentCategory): string {
    switch (category) {
      case 0:
        return 'security';
      case 2:
        return 'bugfix';
      case 3:
        return 'improvement';
      default:
        return 'feature';
    }
  }

  protected formatReleaseDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
  }

  protected safeHtml(slide: ReleaseNoteContentDto): string {
    return DOMPurify.sanitize(slide.htmlContent, {
      USE_PROFILES: { html: true },
      ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h2', 'h3', 'ol', 'ul', 'li', 'a', 'img'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel'],
      FORBID_TAGS: ['script', 'iframe', 'video', 'style', 'form', 'input', 'object', 'embed'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'style'],
    });
  }

  protected selectedSlideIndex(): number {
    const selected = this.selectedSlide();
    return selected ? this.orderedSlides().findIndex((slide) => slide.releaseNoteContentId === selected.releaseNoteContentId) : -1;
  }

  protected canGoPrevious(): boolean {
    return this.selectedSlideIndex() > 0;
  }

  protected canGoNext(): boolean {
    const index = this.selectedSlideIndex();
    return index > -1 && index < this.orderedSlides().length - 1;
  }

  private async loadReleaseNote(currentUser: CurrentUserDto): Promise<void> {
    if (!currentUser.id || this.loading()) {
      return;
    }

    this.loadedForUserId = currentUser.id;
    this.loading.set(true);

    try {
      const notes = await firstValueFrom(this.releaseNoteApi.getWhatsNew(currentUser.appUser?.lastSeenReleaseNoteVersion));
      const note = notes[0] ?? null;
      this.releaseNote.set(note);
      this.selectedSlideId.set(note?.contents[0]?.releaseNoteContentId ?? null);
      this.activeFilter.set('All');
      this.detailsOpen.set(false);
      this.doNotShowAgain.set(false);
      this.open.set(!!note && note.contents.length > 0);
    } catch {
      this.open.set(false);
    } finally {
      this.loading.set(false);
    }
  }

  private moveSlide(direction: 1 | -1): void {
    const slides = this.orderedSlides();
    const currentIndex = this.selectedSlideIndex();
    const nextIndex = currentIndex + direction;
    const nextSlide = slides[nextIndex];
    if (!nextSlide) {
      return;
    }

    this.selectedSlideId.set(nextSlide.releaseNoteContentId);
  }

  private async acknowledgeIfNeeded(): Promise<void> {
    const note = this.releaseNote();
    const user = this.currentUserService.currentUser();
    if (!this.doNotShowAgain() || !note?.version || !user?.id) {
      return;
    }

    try {
      await firstValueFrom(this.userApi.update(user.id, { lastSeenReleaseNoteVersion: note.version }));
      this.currentUserService.clearCache();
    } catch {
      // Best effort only; the modal should still close for the current session.
    }
  }
}
